import { useMemo } from 'react'

import { toRouteDayMeta, type RouteDayMeta } from '../routeDayMeta'

import type { WorkOrderDateFacetApi } from 'types/api/work-order/workOrderFacets'

const mapCounts = (rowsByDate: Record<string, WorkOrderDateFacetApi>, pick: (row: WorkOrderDateFacetApi) => number) => {
  const grouped: Record<string, number> = {}
  Object.entries(rowsByDate).forEach(([serviceDate, row]) => {
    const value = pick(row)
    if (value > 0) grouped[serviceDate] = value
  })
  return grouped
}

/**
 * Re-keys the account's `byDate` facet rows by date for EventsCalendar display. Filtering is
 * handled server-side by the facets request built in useCalendarData.
 */
export const useWorkOrderData = (isDateInVacationHold: (date: string) => boolean, byDate: WorkOrderDateFacetApi[] = []) => {
  const rowsByDate = useMemo(() => {
    const rows: Record<string, WorkOrderDateFacetApi> = {}
    byDate.forEach(row => {
      rows[row.serviceDate] = row
    })
    return rows
  }, [byDate])

  const workOrderCountByDate = useMemo(() => mapCounts(rowsByDate, row => row.count), [rowsByDate])
  const exceptionsByDate = useMemo(() => mapCounts(rowsByDate, row => row.exceptionCount), [rowsByDate])
  const invoicesByDate = useMemo(() => mapCounts(rowsByDate, row => row.invoicedCount), [rowsByDate])
  const disposalTicketsByDate = useMemo(() => mapCounts(rowsByDate, row => row.disposalTicketCount), [rowsByDate])
  const feesByDate = useMemo(() => mapCounts(rowsByDate, row => row.feeCount), [rowsByDate])
  const notesByDate = useMemo(() => mapCounts(rowsByDate, row => row.notesCount), [rowsByDate])

  const routesByDate = useMemo(() => {
    const grouped: Record<string, RouteDayMeta> = {}
    byDate.forEach(row => {
      grouped[row.serviceDate] = toRouteDayMeta(row)
    })
    return grouped
  }, [byDate])

  const workOrderStatusesByDate = useMemo(() => {
    const grouped: Record<string, string[]> = {}
    byDate.forEach(row => {
      const statuses = new Set(row.statuses.map(status => (status === 'Hold' ? 'VacationHold' : status)))
      if (isDateInVacationHold(row.serviceDate)) statuses.add('VacationHold')
      grouped[row.serviceDate] = Array.from(statuses)
    })
    return grouped
  }, [byDate, isDateInVacationHold])

  return {
    workOrderCountByDate,
    exceptionsByDate,
    invoicesByDate,
    disposalTicketsByDate,
    feesByDate,
    notesByDate,
    routesByDate,
    workOrderStatusesByDate,
    rowsByDate,
  }
}
